import { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Ship,
  CalendarDays,
  ClipboardList,
  Users,
  Settings as SettingsIcon,
  LogOut,
  Menu,
  X,
  ArrowUpRight,
} from "lucide-react";
import { clearAdminToken, adminApi } from "../../lib/adminApi";
import HbeLogo from "../HbeLogo";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, exact: true, testid: "nav-dashboard" },
  { to: "/admin/yachts", label: "Yachts", icon: Ship, testid: "nav-yachts" },
  { to: "/admin/bookings", label: "Bookings", icon: ClipboardList, testid: "nav-bookings" },
  { to: "/admin/calendar", label: "Calendar", icon: CalendarDays, testid: "nav-calendar" },
  { to: "/admin/customers", label: "Customers", icon: Users, testid: "nav-customers" },
  { to: "/admin/settings", label: "Settings", icon: SettingsIcon, testid: "nav-settings" },
];

export default function AdminLayout({ children }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [stripeStatus, setStripeStatus] = useState(null);
  const location = useLocation();
  const nav = useNavigate();

  useEffect(() => setMobileOpen(false), [location.pathname]);

  useEffect(() => {
    adminApi.get("/admin/stripe/status")
      .then((r) => setStripeStatus(r.data))
      .catch(() => setStripeStatus(null));
  }, []);

  function logout() {
    clearAdminToken();
    nav("/admin/login", { replace: true });
  }

  const stripeMode = stripeStatus?.mode;
  const stripeConnected = !!stripeStatus?.connected;
  const badge = (() => {
    if (!stripeStatus) return null;
    if (stripeMode === "live" && stripeConnected) {
      return { color: "bg-red-600", label: "LIVE MODE", pulse: true, testid: "stripe-badge-live" };
    }
    if (stripeMode === "live" && !stripeConnected) {
      return { color: "bg-red-500", label: "LIVE · key error", pulse: false, testid: "stripe-badge-live-error" };
    }
    if (stripeMode === "test") {
      return { color: "bg-amber-500", label: "TEST MODE", pulse: false, testid: "stripe-badge-test" };
    }
    return { color: "bg-neutral-500", label: "STRIPE OFF", pulse: false, testid: "stripe-badge-off" };
  })();

  const modeBadge = badge && (
    <div
      className="inline-flex items-center gap-1.5 rounded-full border border-hbe-line bg-white px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest text-hbe-primary"
      data-testid={badge.testid}
      title={stripeStatus?.error || `Stripe ${stripeMode}${stripeConnected ? " · connected" : ""}`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${badge.color} ${badge.pulse ? "animate-pulse" : ""}`} />
      {badge.label}
    </div>
  );

  return (
    <div className="min-h-screen bg-white text-hbe-ink" data-testid="admin-layout">
      {/* Mobile top bar */}
      <div className="lg:hidden sticky top-0 z-40 bg-white border-b border-hbe-line flex items-center justify-between px-4 py-3">
        <Link
          to="/admin"
          className="inline-flex items-center gap-2 text-hbe-primary"
          aria-label="HBE Admin home"
        >
          <HbeLogo variant="mark" size={40} />
          <span className="text-[10px] uppercase tracking-widest text-hbe-accent">Admin</span>
        </Link>
        <div className="flex items-center gap-2">
          {modeBadge}
          <button
            type="button"
            className="inline-flex items-center justify-center min-w-[44px] min-h-[44px] text-hbe-primary"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
            data-testid="admin-mobile-toggle"
          >
            {mobileOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="lg:hidden border-b border-hbe-line bg-white" data-testid="admin-mobile-menu">
          <nav className="px-4 py-2">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.exact}
                data-testid={`admin-m-${n.testid}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 py-3 text-sm ${
                    isActive ? "text-hbe-primary font-medium" : "text-hbe-ink/75"
                  }`
                }
              >
                <n.icon size={18} /> {n.label}
              </NavLink>
            ))}
            <button
              type="button"
              onClick={logout}
              className="flex items-center gap-3 py-3 text-sm text-red-700 w-full text-left"
              data-testid="admin-m-logout"
            >
              <LogOut size={18} /> Logout
            </button>
          </nav>
        </div>
      )}

      <div className="lg:grid lg:grid-cols-[240px_1fr]">
        {/* Desktop sidebar */}
        <aside
          className="hidden lg:flex lg:flex-col border-r border-hbe-line min-h-screen sticky top-0"
          data-testid="admin-sidebar"
        >
          <div className="p-6 pb-4">
            <Link
              to="/admin"
              className="inline-flex items-center gap-2 text-hbe-primary"
              aria-label="HBE Admin home"
            >
              <HbeLogo variant="mark" size={40} />
              <span className="text-[10px] uppercase tracking-widest text-hbe-accent">Admin</span>
            </Link>
            <div className="mt-2 flex items-center gap-2">
              <Link
                to="/"
                target="_blank"
                className="inline-flex items-center gap-1 text-xs text-hbe-accent hover:text-hbe-primary"
                data-testid="admin-view-public"
              >
                View public site <ArrowUpRight size={12} />
              </Link>
            </div>
            {modeBadge && <div className="mt-3">{modeBadge}</div>}
          </div>
          <nav className="flex-1 px-3">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.exact}
                data-testid={`admin-${n.testid}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm mb-0.5 transition ${
                    isActive
                      ? "bg-hbe-soft text-hbe-primary font-medium"
                      : "text-hbe-ink/75 hover:bg-hbe-soft/60 hover:text-hbe-primary"
                  }`
                }
              >
                <n.icon size={16} /> {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="p-3 border-t border-hbe-line">
            <button
              type="button"
              onClick={logout}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm text-red-700 hover:bg-red-50 transition"
              data-testid="admin-logout"
            >
              <LogOut size={16} /> Logout
            </button>
          </div>
        </aside>

        <main className="min-w-0" data-testid="admin-main">
          <div className="max-w-[1200px] mx-auto p-6 md:p-10">{children}</div>
        </main>
      </div>
    </div>
  );
}
